import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { Button } from "@/components/ui/button";
import { Link } from "@tanstack/react-router";
import { getComplianceOverview, runComplianceScan } from "@/lib/compliance-dashboard.functions";
import { COMPANY } from "@/config/company";

export const Route = createFileRoute("/admin/compliance")({
  head: () => ({
    meta: [
      { title: "Compliance · Admin" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: AdminCompliance,
});

function Stat({ label, value, tone = "" }: { label: string; value: string | number; tone?: string }) {
  return (
    <div className="rounded-xl border border-border bg-background p-4">
      <div className="text-xs uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className={`mt-1 text-2xl font-black ${tone}`}>{value}</div>
    </div>
  );
}

function AdminCompliance() {
  const fetchOverview = useServerFn(getComplianceOverview);
  const scan = useServerFn(runComplianceScan);
  const qc = useQueryClient();

  const { data, isLoading, error } = useQuery({
    queryKey: ["compliance-overview"],
    queryFn: () => fetchOverview(),
  });

  const scanMutation = useMutation({
    mutationFn: () => scan(),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["compliance-overview"] }),
  });

  return (
    <div>
      <h1 className="text-2xl font-black tracking-tight">Compliance</h1>
      <p className="mt-1 max-w-3xl text-sm text-muted-foreground">
        Every blog post, social draft, page advertisement and email runs through the pre-publish
        compliance gate. Results are recorded here as pass, warning or blocked. Blocked content
        cannot be published.
      </p>

      <div className="mt-4 flex flex-wrap items-center gap-3">
        <Button onClick={() => scanMutation.mutate()} disabled={scanMutation.isPending}>
          {scanMutation.isPending ? "Scanning…" : "Scan content library"}
        </Button>
        <span className="text-xs text-muted-foreground">
          Last compliance review of site copy: {COMPANY.lastComplianceReviewDate}
        </span>
      </div>

      {isLoading ? <p className="mt-6 text-sm text-muted-foreground">Loading…</p> : null}
      {error ? (
        <p className="mt-6 text-sm text-destructive">Could not load: {(error as Error).message}</p>
      ) : null}

      {data ? (
        <>
          <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <Stat label="Passed checks" value={data.counts.pass} tone="text-emerald-600" />
            <Stat label="Warnings" value={data.counts.warning} tone="text-yellow-600" />
            <Stat label="Blocked" value={data.counts.blocked} tone="text-destructive" />
            <Stat
              label={`Archived ads (${data.archive.retentionYears}-yr retention)`}
              value={data.archive.records}
            />
          </div>

          {data.missingCompanyFields.length > 0 ? (
            <div className="mt-6 rounded-xl border border-yellow-500/50 bg-yellow-500/5 p-4">
              <h2 className="text-sm font-semibold">Missing company information</h2>
              <p className="mt-1 text-xs text-muted-foreground">
                Advertising that legally requires these fields is blocked until they are supplied.
              </p>
              <ul className="mt-3 space-y-2 text-sm">
                {data.missingCompanyFields.map((f) => (
                  <li key={f.field}>
                    <strong>{f.field}</strong> — {f.whyItMatters}
                  </li>
                ))}
              </ul>
            </div>
          ) : null}

          {scanMutation.data ? (
            <div className="mt-6 rounded-xl border border-border bg-muted/30 p-4 text-sm">
              Scanned {scanMutation.data.blogsScanned} blog posts and{" "}
              {scanMutation.data.socialScanned} social drafts — {scanMutation.data.passed} passed,{" "}
              {scanMutation.data.warnings} with warnings, {scanMutation.data.blocked} blocked.
            </div>
          ) : null}

          <h2 className="mt-8 text-lg font-bold">Compliance log</h2>
          <div className="mt-3 space-y-3">
            {data.recent.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                No checks recorded yet. Run a scan or publish content to populate the log.
              </p>
            ) : null}
            {data.recent.map((row) => (
              <div key={row.id} className="rounded-xl border border-border p-4">
                <div className="flex flex-wrap items-center gap-2 text-xs">
                  <span
                    className={`rounded-full px-2 py-0.5 font-semibold uppercase ${
                      row.result === "blocked"
                        ? "bg-destructive/15 text-destructive"
                        : row.result === "warning"
                          ? "bg-yellow-500/15 text-yellow-800"
                          : "bg-emerald-500/15 text-emerald-700"
                    }`}
                  >
                    {row.result}
                  </span>
                  <span className="text-muted-foreground">{row.surface}</span>
                  <span className="text-muted-foreground">
                    {new Date(row.checked_at).toLocaleString()}
                  </span>
                </div>
                <div className="mt-1 text-sm font-semibold">{row.subject}</div>
                {row.surface === "blog" && row.subject_ref ? (
                  <Link
                    to="/admin/posts/$id"
                    params={{ id: row.subject_ref }}
                    className="text-xs text-brand underline"
                  >
                    Open post
                  </Link>
                ) : null}
                {row.url ? (
                  <a href={row.url} className="ml-3 text-xs text-brand underline">
                    {row.url}
                  </a>
                ) : null}
                {row.issues?.length ? (
                  <ul className="mt-2 space-y-1 text-xs text-muted-foreground">
                    {row.issues.slice(0, 8).map((i, idx) => (
                      <li key={idx}>
                        <strong className={i.level === "block" ? "text-destructive" : ""}>
                          {i.level === "block" ? "BLOCKED" : "WARNING"}
                        </strong>{" "}
                        — {i.message} <em>({i.rule})</em>
                      </li>
                    ))}
                  </ul>
                ) : null}
              </div>
            ))}
          </div>
        </>
      ) : null}
    </div>
  );
}
